/**
 * A tiny 2D physics integrator for the two Venn-mark discs rendered inside
 * the assistant launcher puck. Pure — no React, no DOM — so it can run
 * inside a `requestAnimationFrame` loop and be unit tested in isolation.
 *
 * Each disc is a loose ball bouncing inside the puck's circular wall (with
 * restitution off the wall and off the other disc), pulled by a gentle
 * "gravity" spring back toward its own logo rest position — weak enough that
 * a drag or fling genuinely throws it across the puck's interior and it
 * bounces a few times, strong enough that it always reassembles into the
 * exact mark at rest.
 */

import type { Point } from './cornerPin';

export type Body = { x: number; y: number; vx: number; vy: number };

/**
 * A one-shot impulse queued for the physics loop to consume on its next
 * tick. `translational` adds the same vector to both bodies (e.g. the
 * corner-landing "thunk"); `radial` pushes each body along its own vector
 * from `PUCK_CENTER` — positive magnitude pops the discs apart, negative
 * pulls them together — which a single shared vector can't express.
 */
export type PendingImpulse =
  | { kind: 'translational'; vector: Point }
  | { kind: 'radial'; magnitude: number };

/** Logo rest positions in the mark's `viewBox="0 0 80 80"` SVG units. */
export const REST_POSITIONS: readonly [Point, Point] = [
  { x: 28, y: 40 },
  { x: 52, y: 40 },
];

/** Gentle homing pull toward each disc's own rest position ("gravity"). */
const SPRING_K = 26;
/** Low damping so bounces persist and decay naturally instead of dying instantly. */
const DAMPING = 5;
const REST_SEPARATION = 24;
/** Bounciness for both the wall and disc-disc collisions. */
const RESTITUTION = 0.7;

/** Centre of the puck's circular wall, in viewBox units — concentric with the
 * puck itself (`viewBox="0 0 80 80"`). */
const PUCK_CENTER: Point = { x: 40, y: 40 };
/** Disc radius in viewBox units — also the `r` on the five circles LogoMarkPhysics
 * renders, so retuning it can never desync the rendered geometry from the wall math. */
export const DISC_RADIUS = 22;
/** The puck's inner edge — the wall the discs bounce off. */
const WALL_RADIUS = 40;
/** Max distance a disc's centre may be from `PUCK_CENTER` before it bounces
 * off the wall (`WALL_RADIUS - DISC_RADIUS`). */
const MAX_CENTER_DIST = WALL_RADIUS - DISC_RADIUS;

const REST_EPSILON = 0.05;
const SPEED_EPSILON = 0.05;

export function createBodies(): [Body, Body] {
  return [
    { x: REST_POSITIONS[0].x, y: REST_POSITIONS[0].y, vx: 0, vy: 0 },
    { x: REST_POSITIONS[1].x, y: REST_POSITIONS[1].y, vx: 0, vy: 0 },
  ];
}

/**
 * Semi-implicit Euler step. `accel` is the puck's acceleration in SVG
 * units/s^2 (already scaled by the caller); it is applied as a pseudo-force
 * `-m * accel` (mass 1) so the discs lag opposite the puck's motion.
 */
export function stepBodies(bodies: [Body, Body], accel: Point, dt: number): void {
  for (let i = 0; i < 2; i++) {
    const body = bodies[i];
    const rest = REST_POSITIONS[i];

    const fx = -SPRING_K * (body.x - rest.x) - DAMPING * body.vx - accel.x;
    const fy = -SPRING_K * (body.y - rest.y) - DAMPING * body.vy - accel.y;

    body.vx += fx * dt;
    body.vy += fy * dt;
    body.x += body.vx * dt;
    body.y += body.vy * dt;
  }

  resolveDiscCollision(bodies);
  resolveWallContainment(bodies);
}

function resolveDiscCollision(bodies: [Body, Body]): void {
  const [a, b] = bodies;
  const dx = b.x - a.x;
  const dy = b.y - a.y;
  let distance = Math.sqrt(dx * dx + dy * dy);

  if (distance >= REST_SEPARATION) return;

  let nx: number;
  let ny: number;
  if (distance < 1e-6) {
    // Degenerate zero-distance case — push apart along a fixed axis.
    nx = 1;
    ny = 0;
    distance = 0;
  } else {
    nx = dx / distance;
    ny = dy / distance;
  }

  const overlap = REST_SEPARATION - distance;
  const push = overlap / 2;
  a.x -= nx * push;
  a.y -= ny * push;
  b.x += nx * push;
  b.y += ny * push;

  // Exchange the normal component of relative velocity with restitution.
  const relVx = b.vx - a.vx;
  const relVy = b.vy - a.vy;
  const relNormal = relVx * nx + relVy * ny;
  if (relNormal < 0) {
    const impulse = -(1 + RESTITUTION) * relNormal * 0.5;
    a.vx -= impulse * nx;
    a.vy -= impulse * ny;
    b.vx += impulse * nx;
    b.vy += impulse * ny;
  }
}

/**
 * Bounces a disc off the puck's circular wall — a shared boundary centred on
 * the puck itself, not a per-disc leash — so a thrown disc can cross the
 * puck's interior and rebound off the far side instead of being tethered
 * near its own rest position.
 *
 * This clamps position and reflects velocity *after* the Euler step, so in
 * principle a fast enough disc could displace past the wall in one step and
 * land back inside on the far side without ever triggering the reflection
 * (discrete "tunneling"). Safe here because the caller clamps acceleration
 * to `ACCEL_CLAMP` (see `useCornerPin.ts`), which keeps per-step
 * displacement (measured worst case ~7.5 units) far below the wall's
 * 36-unit diameter — raising that clamp is what would need re-checking this.
 */
function resolveWallContainment(bodies: [Body, Body]): void {
  for (const body of bodies) {
    const dx = body.x - PUCK_CENTER.x;
    const dy = body.y - PUCK_CENTER.y;
    const distance = Math.sqrt(dx * dx + dy * dy);
    if (distance <= MAX_CENTER_DIST) continue;

    let nx: number;
    let ny: number;
    if (distance < 1e-6) {
      // Degenerate zero-distance case — push out along a fixed axis.
      nx = 1;
      ny = 0;
    } else {
      nx = dx / distance;
      ny = dy / distance;
    }

    body.x = PUCK_CENTER.x + nx * MAX_CENTER_DIST;
    body.y = PUCK_CENTER.y + ny * MAX_CENTER_DIST;

    const radialSpeed = body.vx * nx + body.vy * ny;
    if (radialSpeed > 0) {
      const reflected = radialSpeed * (1 + RESTITUTION);
      body.vx -= reflected * nx;
      body.vy -= reflected * ny;
    }
  }
}

export function isAtRest(bodies: [Body, Body]): boolean {
  for (let i = 0; i < 2; i++) {
    const body = bodies[i];
    const rest = REST_POSITIONS[i];
    const dx = body.x - rest.x;
    const dy = body.y - rest.y;
    const distanceFromRest = Math.sqrt(dx * dx + dy * dy);
    const speed = Math.sqrt(body.vx * body.vx + body.vy * body.vy);
    if (distanceFromRest > REST_EPSILON || speed > SPEED_EPSILON) return false;
  }
  return true;
}

/** Apply an instantaneous velocity impulse to both bodies (e.g. a landing thunk). */
export function applyImpulse(bodies: [Body, Body], impulse: Point): void {
  for (const body of bodies) {
    body.vx += impulse.x;
    body.vy += impulse.y;
  }
}

/**
 * Push both discs radially away from (positive `magnitude`) or toward
 * (negative `magnitude`) the puck centre — the "pop apart" / "collapse
 * together" gestures for opening/dismissing the assistant. Each disc moves
 * along its own vector from `PUCK_CENTER`, which a single shared vector
 * (`applyImpulse`) can't express.
 */
export function applyRadialImpulse(bodies: [Body, Body], magnitude: number): void {
  for (let i = 0; i < 2; i++) {
    const body = bodies[i];
    let dx = body.x - PUCK_CENTER.x;
    let dy = body.y - PUCK_CENTER.y;
    let distance = Math.sqrt(dx * dx + dy * dy);

    if (distance < 1e-6) {
      // Degenerate: the body sits exactly at the puck centre — fall back to
      // the direction of its own rest position from the centre, which is
      // always non-zero for both discs.
      const rest = REST_POSITIONS[i];
      dx = rest.x - PUCK_CENTER.x;
      dy = rest.y - PUCK_CENTER.y;
      distance = Math.sqrt(dx * dx + dy * dy);
    }

    const nx = dx / distance;
    const ny = dy / distance;
    body.vx += nx * magnitude;
    body.vy += ny * magnitude;
  }
}
